import useEventCallback from '@unifyapps/hooks/useEventCallback';
import SvgChevronRight from '@unifyapps/icons/outline/ChevronRight';
import { Box } from '@unifyapps/ui/components/Box';
import { IconButton } from '@unifyapps/ui/components/IconButton';
import { Popover } from '@unifyapps/ui/components/Popover';
import { Stack } from '@unifyapps/ui/components/Stack';
import { Typography } from '@unifyapps/ui/components/Typography';
import { clsx } from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useCollapse } from 'react-collapsed';
import { EMPTY_OBJECT } from '../../../../consts/empty';
import { UA_SIDE_NAV_ITEM } from '../../constants';
import {
  type NavApplicationItemType,
  type NavItem,
  type NavItemGroup,
  NavigationItemSelectionVariantEnum,
  type NavigationItemSlotPropsType,
} from '../../types';
import { NavigationIcon } from '../NavigationIcon';
import {
  FLYOUT_OFFSET,
  FLYOUT_OFFSET_BOTTOM,
  NavigationGroupFlyout,
  useFlyoutPanelClassName,
} from './NavigationGroupFlyout';
import NavigationSubItem from './NavigationSubItem';
import { chevronVariants, iconVariants, labelVariants, rootVariants } from './styles';

type Props = {
  item: NavItemGroup;
  isGroupOpen?: boolean;
  isGroupActive?: boolean;
  activeNavItem?: NavItem | NavApplicationItemType;
  slotProps?: NavigationItemSlotPropsType;
};

export const NavigationGroup = (props: Props) => {
  const { isGroupActive = false, item, activeNavItem, slotProps } = props;
  const { id, items, name, icon } = item;

  const {
    expanded: isSideNavExpanded = true,
    expandedGroupId,
    setExpandedGroupId,
    navItemPopover,
    setNavItemPopover,
  } = slotProps?.navigationItem ?? (EMPTY_OBJECT as NavigationItemSlotPropsType['navigationItem']);

  const [activeSubGroupId, setActiveSubGroupId] = useState<string | null>(null);
  const flyoutPanelClassName = useFlyoutPanelClassName();

  // Placement is resolved ONCE per open, from the trigger's vertical position: bottom-half
  // triggers anchor `right-end` so the panel grows upward. A fixed placement is what prevents
  // the flicker — expanding an L2 sub-group animates the panel's height, and if flip had to
  // re-place the overflowing panel it would oscillate between placements every animation frame.
  const flyoutHostRef = useRef<HTMLDivElement | null>(null);
  const [isFlyoutBottomAnchored, setIsFlyoutBottomAnchored] = useState(false);

  const resolveFlyoutPlacement = useEventCallback(() => {
    const hostRect = flyoutHostRef.current?.getBoundingClientRect();
    if (hostRect) {
      setIsFlyoutBottomAnchored(hostRect.top + hostRect.height / 2 > window.innerHeight / 2);
    }
  });

  // collapsed-rail popover open model: hover is transient, click "pins". `navItemPopover` is the
  // single shared open slot (useSideNavigation), so at most one popover is open — crossing from one
  // group to the next can't leave both open while a previous popover's 200ms hover-close debounce is
  // still pending. `groupId` decides what's open; `isPinned` only changes whether pointer-leave
  // closes it.
  const isPopoverOpen = navItemPopover?.groupId === id;
  const isPopoverPinned = isPopoverOpen && Boolean(navItemPopover?.isPinned);

  const handlePopoverOpenChange = useEventCallback((next: boolean) => {
    if (next) {
      resolveFlyoutPlacement();
      // claim the shared slot as hover-open, but don't downgrade an existing pin on this group.
      setNavItemPopover?.((prev) =>
        prev.groupId === id && prev.isPinned ? prev : { groupId: id, isPinned: false },
      );
      return;
    }
    // hover-out: only release if this group still owns the slot AND it isn't pinned. The functional
    // guard prevents stomping a popover the pointer has already moved to, or closing our own pin.
    setNavItemPopover?.((prev) =>
      prev.groupId === id && !prev.isPinned ? { groupId: null, isPinned: false } : prev,
    );
  });

  const handlePopoverClose = useEventCallback(() => {
    // outside-click / Esc: release only if this group still owns the slot.
    setNavItemPopover?.((prev) =>
      prev.groupId === id ? { groupId: null, isPinned: false } : prev,
    );
  });

  const handlePopoverClick = useEventCallback(() => {
    if (isPopoverPinned) {
      // unpin on re-click; closes immediately even while still hovered.
      setNavItemPopover?.({ groupId: null, isPinned: false });
      return;
    }
    resolveFlyoutPlacement();
    setNavItemPopover?.({ groupId: id, isPinned: true });
  });

  const isGroupExpanded = item.id === expandedGroupId;

  const onClick = useEventCallback(() => {
    const updatedValue = isGroupExpanded ? null : id;
    setExpandedGroupId?.(updatedValue);
  });

  const { getCollapseProps, getToggleProps } = useCollapse({
    isExpanded: isGroupExpanded,
    easing: 'ease-in-out',
    duration: 150,
  });

  useEffect(() => {
    if (expandedGroupId === null && activeSubGroupId !== null) {
      setActiveSubGroupId(null);
    }
  }, [expandedGroupId, activeSubGroupId]);

  const hasChildren = Boolean(items.length);

  const getIconButton = () => {
    const iconClassName = iconVariants({ active: isGroupActive });
    const labelClassName = labelVariants({ active: isGroupActive, isSideNavExpanded });

    const chevronClassName = chevronVariants({
      open: isGroupExpanded,
      active: isGroupActive,
      isSideNavExpanded,
    });

    const rootClassname = rootVariants({
      active: isGroupActive,
      expanded: isGroupExpanded,
      isSideNavExpanded,
      className: `${UA_SIDE_NAV_ITEM} ua-platform-side-nav-flyout-button group/PlatformSideNavNestedItem`,
    });

    return (
      <Stack
        alignItems="center"
        className={rootClassname}
        component="button"
        direction="row"
        // toggle props inline-expand the L2 collapse region; only relevant in the expanded rail
        {...(isSideNavExpanded ? getToggleProps() : {})}
        // collapsed rail: click pins/unpins the hover flyout (PopoverButton forwards this onClick).
        onClick={isSideNavExpanded ? onClick : handlePopoverClick}
      >
        {/* 9px gap: label lands 36px from the item edge (9px pad + 18px glyph + 9px), matching
            Figma's 8px pad + 20px icon box + 8px gap. Keep in sync with buttonNavItemRootVariants. */}
        <Box className="flex flex-row gap-x-lg items-center">
          <NavigationIcon
            className={iconClassName}
            iconName={icon}
            label={name}
            selectionVariant={NavigationItemSelectionVariantEnum.Stroke}
          />
          {isSideNavExpanded && (
            <Typography
              as="span"
              className={labelClassName}
              maxNumberOfLines={1}
              variant="text-sm"
              weight="medium"
            >
              {name}
            </Typography>
          )}
        </Box>
        {isSideNavExpanded && (
          <IconButton
            aria-hidden
            className="ua-platform-side-nav-chevron-button !size-5 !rounded-xs !pe-none"
            color="neutral"
            component="span"
            Icon={SvgChevronRight}
            iconClassName={clsx(
              isGroupActive ? 'ua-side-nav-item-active' : 'ua-side-nav-item-active-false',
              chevronClassName,
            )}
            size="sm"
            variant="ghost"
          />
        )}
      </Stack>
    );
  };

  const iconButton = getIconButton();

  // collapsed rail: there is no room for the inline collapse region, so the L2
  // children open in a hover flyout anchored to the right of the rail instead.
  if (!isSideNavExpanded) {
    if (!hasChildren) {
      return <Stack className="gap-y-xs">{iconButton}</Stack>;
    }

    return (
      <Stack className="gap-y-xs ua-platform-side-nav-flyout-host" ref={flyoutHostRef}>
        <Popover
          onClose={handlePopoverClose}
          onOpenChange={handlePopoverOpenChange}
          open={isPopoverOpen}
          triggerType="hover"
        >
          <Popover.Button>{iconButton}</Popover.Button>
          <Popover.Content
            className={flyoutPanelClassName}
            minWidth={185 / 16}
            offset={isFlyoutBottomAnchored ? FLYOUT_OFFSET_BOTTOM : FLYOUT_OFFSET}
            placement={isFlyoutBottomAnchored ? 'right-end' : 'right-start'}
            useAnchorAsContainer
          >
            <NavigationGroupFlyout activeNavItem={activeNavItem} item={item} />
          </Popover.Content>
        </Popover>
      </Stack>
    );
  }

  return (
    <Stack className="gap-y-xxs">
      {iconButton}

      <Box {...getCollapseProps()}>
        <Stack className="ps-8 gap-y-xs pb-xxs" direction="column">
          {items.map((subItem) => {
            return (
              <NavigationSubItem
                activeNavItem={activeNavItem}
                activeSubGroupId={activeSubGroupId}
                item={subItem}
                key={subItem.id}
                setActiveSubGroupId={setActiveSubGroupId}
                slotProps={slotProps}
              />
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
};
